import React, { useMemo, useState } from 'react'
import { Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { sortRows } from './api'
import { useLedger } from './LedgerContext'

const toSkDT = (iso) => iso ? new Date(iso).toLocaleString('sk-SK') : '-'

export default function PayoutsPanel({ payoutRows, loadingPayouts, reload, limit }) {
  const { sortPayout, setSortPayout, exportCSV, exportXLSX, exportPDF } = useLedger()
  const [pagePayout, setPagePayout] = useState(1)
  const [filters, setFilters] = useState({
    id: '', userId: '', email: '', meno: '', priezvisko: '',
    iban: '', transaction_ref: '', status: '', failure_reason: ''
  })

  const toggleSortPayout = (field) =>
    setSortPayout(s => s.field === field ? { field, dir: (s.dir === 'asc' ? 'desc' : 'asc') } : { field, dir: 'asc' })

  const payoutRowsFiltered = useMemo(() => {
    return payoutRows.filter(r => {
      if (filters.id && String(r.id ?? '').indexOf(filters.id) === -1) return false
      if (filters.userId && String(r.userId ?? '').indexOf(filters.userId) === -1) return false
      if (filters.email && String(r.email ?? '').toLowerCase().indexOf(filters.email.toLowerCase()) === -1) return false
      if (filters.meno && String(r.meno ?? '').toLowerCase().indexOf(filters.meno.toLowerCase()) === -1) return false
      if (filters.priezvisko && String(r.priezvisko ?? '').toLowerCase().indexOf(filters.priezvisko.toLowerCase()) === -1) return false
      if (filters.iban && String(r.iban ?? '').toLowerCase().indexOf(filters.iban.toLowerCase()) === -1) return false
      if (filters.transaction_ref && String(r.transaction_ref ?? '').toLowerCase().indexOf(filters.transaction_ref.toLowerCase()) === -1) return false
      if (filters.status && String(r.status ?? '').toLowerCase() !== filters.status.toLowerCase()) return false
      if (filters.failure_reason && String(r.failure_reason ?? '').toLowerCase().indexOf(filters.failure_reason.toLowerCase()) === -1) return false
      return true
    })
  }, [payoutRows, filters])

  const payoutRowsSorted = useMemo(() => sortRows(payoutRowsFiltered, sortPayout), [payoutRowsFiltered, sortPayout])

  const payoutPageSize = Number(limit) || 50
  const payoutPageSlice = useMemo(() => {
    const start = (Math.max(1, pagePayout) - 1) * payoutPageSize
    return payoutRowsSorted.slice(start, start + payoutPageSize)
  }, [payoutRowsSorted, pagePayout, payoutPageSize])

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6">Payouts</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => exportCSV({})}>Export CSV</Button>
            <Button variant="outlined" onClick={() => exportXLSX({})}>Export XLSX</Button>
            <Button variant="outlined" onClick={() => exportPDF({})}>Export PDF</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
            <Button variant="outlined" disabled={pagePayout <= 1} onClick={() => setPagePayout(p => Math.max(1, p - 1))}>Predchádzajúca</Button>
            <Button variant="outlined" disabled={payoutPageSlice.length < payoutPageSize} onClick={() => setPagePayout(p => p + 1)}>Ďalšia</Button>
            <Box sx={{ alignSelf: 'center', ml: 1 }}>Strana: {pagePayout}</Box>
            <Button variant="text" onClick={reload}>Obnoviť</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField label="ID" value={filters.id} onChange={e => setFilters(f => ({ ...f, id: e.target.value }))} />
          <TextField label="User ID" value={filters.userId} onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))} />
          <TextField label="Email" value={filters.email} onChange={e => setFilters(f => ({ ...f, email: e.target.value }))} />
          <TextField label="Meno" value={filters.meno} onChange={e => setFilters(f => ({ ...f, meno: e.target.value }))} />
          <TextField label="Priezvisko" value={filters.priezvisko} onChange={e => setFilters(f => ({ ...f, priezvisko: e.target.value }))} />
          <TextField label="IBAN" value={filters.iban} onChange={e => setFilters(f => ({ ...f, iban: e.target.value }))} />
          <TextField label="Transaction Ref" value={filters.transaction_ref} onChange={e => setFilters(f => ({ ...f, transaction_ref: e.target.value }))} />
          <TextField label="Failure reason" value={filters.failure_reason} onChange={e => setFilters(f => ({ ...f, failure_reason: e.target.value }))} />
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel id="filter-status-label">Status</InputLabel>
            <Select
              labelId="filter-status-label"
              value={filters.status}
              label="Status"
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">— Všetko —</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Button variant="text" onClick={() => setFilters({
            id: '', userId: '', email: '', meno: '', priezvisko: '',
            iban: '', transaction_ref: '', status: '', failure_reason: ''
          })}>Reset</Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 520, mt: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell onClick={() => toggleSortPayout('id')} sx={{ cursor: 'pointer' }}>ID</TableCell>
              <TableCell onClick={() => toggleSortPayout('amount')} sx={{ cursor: 'pointer' }} align="right">Amount</TableCell>
              <TableCell onClick={() => toggleSortPayout('transaction_ref')} sx={{ cursor: 'pointer' }}>Transaction Ref</TableCell>
              <TableCell onClick={() => toggleSortPayout('paid_at')} sx={{ cursor: 'pointer' }}>Paid At</TableCell>
              <TableCell onClick={() => toggleSortPayout('createdAt')} sx={{ cursor: 'pointer' }}>Created</TableCell>
              <TableCell onClick={() => toggleSortPayout('updatedAt')} sx={{ cursor: 'pointer' }}>Updated</TableCell>
              <TableCell onClick={() => toggleSortPayout('userId')} sx={{ cursor: 'pointer' }}>User ID</TableCell>
              <TableCell onClick={() => toggleSortPayout('email')} sx={{ cursor: 'pointer' }}>Email</TableCell>
              <TableCell onClick={() => toggleSortPayout('meno')} sx={{ cursor: 'pointer' }}>Meno</TableCell>
              <TableCell onClick={() => toggleSortPayout('priezvisko')} sx={{ cursor: 'pointer' }}>Priezvisko</TableCell>
              <TableCell onClick={() => toggleSortPayout('iban')} sx={{ cursor: 'pointer' }}>IBAN</TableCell>
              <TableCell onClick={() => toggleSortPayout('status')} sx={{ cursor: 'pointer' }}>Status</TableCell>
              <TableCell onClick={() => toggleSortPayout('failure_reason')} sx={{ cursor: 'pointer' }}>Failure reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payoutRowsFiltered.length === 0 && !loadingPayouts && (
              <TableRow><TableCell colSpan={13}>Žiadne dáta</TableCell></TableRow>
            )}
            {loadingPayouts && (
              <TableRow><TableCell colSpan={13}>Načítavam…</TableCell></TableRow>
            )}
            {payoutPageSlice.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.id}</TableCell>
                <TableCell align="right">{Number(r.amount || 0)}</TableCell>
                <TableCell>{r.transaction_ref || '-'}</TableCell>
                <TableCell>{toSkDT(r.paid_at)}</TableCell>
                <TableCell>{toSkDT(r.createdAt)}</TableCell>
                <TableCell>{toSkDT(r.updatedAt)}</TableCell>
                <TableCell>{r.userId ?? '-'}</TableCell>
                <TableCell>{r.email || '-'}</TableCell>
                <TableCell>{r.meno || '-'}</TableCell>
                <TableCell>{r.priezvisko || '-'}</TableCell>
                <TableCell>{r.iban || '-'}</TableCell>
                <TableCell>{r.status || '-'}</TableCell>
                <TableCell>{r.failure_reason || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}